'use client';

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface LeaveReviewErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  className?: string;
}

interface LeaveReviewErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

export default class LeaveReviewErrorBoundary extends Component<
  LeaveReviewErrorBoundaryProps,
  LeaveReviewErrorBoundaryState
> {
  private maxRetries = 2;

  constructor(props: LeaveReviewErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<LeaveReviewErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('LeaveReview ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        retryCount: prevState.retryCount + 1
      }));
    } else {
      window.location.reload();
    }
  };

  handleGoHome = () => {
    window.location.href = '/dashboard/tenant';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className={`flex items-center justify-center min-h-[400px] p-4 ${this.props.className || ''}`}>
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="h-12 w-12 text-red-500" />
              </div>
              <CardTitle className="text-red-700">Review Flow Error</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 text-center">
                We encountered an issue with your review. Your progress has been saved.
              </p>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={this.handleRetry}
                  variant="default"
                  className="w-full"
                  disabled={this.state.retryCount >= this.maxRetries}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {this.state.retryCount >= this.maxRetries 
                    ? 'Refreshing page...' 
                    : 'Try Again'
                  }
                </Button>

                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="w-full"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Return to Dashboard
                </Button>
              </div>

              {this.state.retryCount > 0 && (
                <p className="text-xs text-gray-500 text-center">
                  Retry attempt {this.state.retryCount} of {this.maxRetries}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
} 